/**
 * Transient focus state: which layer (if any) is currently solo'd.
 * Unlike Visibility, focus is never persisted — it resets on page load.
 */

import type { LngLatBoundsTuple } from '../map/featureBounds.js';
import { CABLE_BOUNDS } from '../layers/cableGeometry.js';

/** Layer IDs that have real map geometry and can be solo-focused. */
export const FOCUSABLE_LAYER_IDS: ReadonlySet<string> = new Set([
  'subway',
  'lirr',
  'mnr',
  'path',
  'buses',
  'ene',
  'ferry',
  // 'ais', // ships disabled for launch
  'citibike',
  'floodnet',
  'c311',
  'flights',
  'power_outages',
  'harbor',
  'grid_load',
]);

/**
 * Focus-camera fallback for layers whose map geometry is a static client-side
 * constant rather than server-provided features — `featuresToBounds` returns
 * null for these (no `LayerState.features`), so main.ts's fit-to-focus needs
 * a bounds lookup instead. Small and generic by design: add an entry here
 * whenever a new focusable layer's geometry isn't in `LayerState.features`.
 */
export const STATIC_FOCUS_BOUNDS: Readonly<Record<string, LngLatBoundsTuple>> = {
  grid_load: CABLE_BOUNDS,
};

export function isFocusable(layerId: string): boolean {
  return FOCUSABLE_LAYER_IDS.has(layerId);
}

/** Tracks which layer (by layerId) is currently solo'd on the map. */
export class Focus {
  private id: string | null = null;
  private listeners = new Set<() => void>();

  get(): string | null {
    return this.id;
  }

  set(id: string | null): void {
    if (this.id === id) return;
    this.id = id;
    for (const cb of this.listeners) cb();
  }

  toggle(id: string): void {
    this.set(this.id === id ? null : id);
  }

  clear(): void {
    this.set(null);
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}
